import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const COOKIE_CONSENT_KEY = 'kaesthetic-cookie-consent';

const COOKIE_TEXT = {
  en: {
    message: 'We use cookies to enhance your browsing experience.',
    accept: 'Accept',
    reject: 'Reject',
    settings: 'Settings',
    necessary: 'Necessary',
    analytics: 'Analytics',
    save: 'Save Preferences',
    necessaryDesc: 'Required for the website to function properly.',
    analyticsDesc: 'Help us understand how visitors interact with our site.',
  },
  es: {
    message: 'Utilizamos cookies para mejorar tu experiencia de navegación.',
    accept: 'Aceptar',
    reject: 'Rechazar',
    settings: 'Configurar',
    necessary: 'Necesarias',
    analytics: 'Analíticas',
    save: 'Guardar Preferencias',
    necessaryDesc: 'Necesarias para el funcionamiento del sitio.',
    analyticsDesc: 'Nos ayudan a entender cómo interactúan los visitantes.',
  },
  ko: {
    message: '더 나은 브라우징 경험을 위해 쿠키를 사용합니다.',
    accept: '수락',
    reject: '거부',
    settings: '설정',
    necessary: '필수',
    analytics: '분석',
    save: '설정 저장',
    necessaryDesc: '웹사이트 정상 작동에 필요합니다.',
    analyticsDesc: '방문자 상호작용을 이해하는 데 도움됩니다.',
  },
  ja: {
    message: 'ブラウジング体験を向上させるためにCookieを使用しています。',
    accept: '同意',
    reject: '拒否',
    settings: '設定',
    necessary: '必須',
    analytics: '分析',
    save: '設定を保存',
    necessaryDesc: 'ウェブサイトの正常な動作に必要です。',
    analyticsDesc: '訪問者の行動を理解するのに役立ちます。',
  },
  zh: {
    message: '我们使用Cookie来提升您的浏览体验。',
    accept: '接受',
    reject: '拒绝',
    settings: '设置',
    necessary: '必要',
    analytics: '分析',
    save: '保存设置',
    necessaryDesc: '网站正常运行所必需的。',
    analyticsDesc: '帮助我们了解访问者如何与网站互动。',
  },
  fr: {
    message: 'Nous utilisons des cookies pour améliorer votre expérience de navigation.',
    accept: 'Accepter',
    reject: 'Refuser',
    settings: 'Paramètres',
    necessary: 'Nécessaires',
    analytics: 'Analytiques',
    save: 'Enregistrer',
    necessaryDesc: 'Nécessaires au bon fonctionnement du site.',
    analyticsDesc: 'Nous aident à comprendre les interactions des visiteurs.',
  },
  pt: {
    message: 'Utilizamos cookies para melhorar a sua experiência de navegação.',
    accept: 'Aceitar',
    reject: 'Rejeitar',
    settings: 'Configurar',
    necessary: 'Necessários',
    analytics: 'Analíticos',
    save: 'Salvar Preferências',
    necessaryDesc: 'Necessários para o funcionamento do site.',
    analyticsDesc: 'Nos ajudam a entender como os visitantes interagem.',
  },
  de: {
    message: 'Wir verwenden Cookies, um Ihr Browsererlebnis zu verbessern.',
    accept: 'Akzeptieren',
    reject: 'Ablehnen',
    settings: 'Einstellungen',
    necessary: 'Notwendig',
    analytics: 'Analytik',
    save: 'Einstellungen speichern',
    necessaryDesc: 'Für das Funktionieren der Website erforderlich.',
    analyticsDesc: 'Helfen uns zu verstehen, wie Besucher interagieren.',
  },
  it: {
    message: 'Utilizziamo i cookie per migliorare la tua esperienza di navigazione.',
    accept: 'Accetta',
    reject: 'Rifiuta',
    settings: 'Impostazioni',
    necessary: 'Necessari',
    analytics: 'Analitici',
    save: 'Salva Preferenze',
    necessaryDesc: 'Necessari per il corretto funzionamento del sito.',
    analyticsDesc: 'Ci aiutano a capire come i visitatori interagiscono.',
  },
  el: {
    message: 'Χρησιμοποιούμε cookies για να βελτιώσουμε την εμπειρία περιήγησής σας.',
    accept: 'Αποδοχή',
    reject: 'Απόρριψη',
    settings: 'Ρυθμίσεις',
    necessary: 'Απαραίτητα',
    analytics: 'Αναλυτικά',
    save: 'Αποθήκευση',
    necessaryDesc: 'Απαραίτητα για τη σωστή λειτουργία του ιστότοπου.',
    analyticsDesc: 'Μας βοηθούν να κατανοήσουμε πώς αλληλεπιδρούν οι επισκέπτες.',
  },
  ru: {
    message: 'Мы используем файлы cookie для улучшения вашего опыта.',
    accept: 'Принять',
    reject: 'Отклонить',
    settings: 'Настройки',
    necessary: 'Необходимые',
    analytics: 'Аналитика',
    save: 'Сохранить',
    necessaryDesc: 'Необходимы для работы сайта.',
    analyticsDesc: 'Помогают нам понять, как посетители взаимодействуют с сайтом.',
  },
};

const CookieConsent = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  const text = COOKIE_TEXT[language] || COOKIE_TEXT.en;

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ necessary: true, analytics: true, timestamp: Date.now() }));
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ necessary: true, analytics: false, timestamp: Date.now() }));
    setVisible(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ necessary: true, analytics: analyticsEnabled, timestamp: Date.now() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] animate-slide-up"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="relative bg-zinc-950 border border-white/10 p-4 sm:p-6 backdrop-blur-xl">
          {/* Close */}
          <button
            onClick={handleReject}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-zinc-600 hover:text-white transition-colors"
            data-testid="cookie-close-btn"
          >
            <X className="w-4 h-4" />
          </button>

          {!showSettings ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 pr-8">
              <p className="text-zinc-400 text-xs sm:text-sm leading-relaxed flex-1">
                {text.message}
              </p>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={() => setShowSettings(true)}
                  className="text-[10px] sm:text-xs tracking-[0.1em] uppercase text-zinc-500 hover:text-white transition-colors px-3 py-2 border border-white/10 hover:border-white/30"
                  data-testid="cookie-settings-btn"
                >
                  {text.settings}
                </button>
                <button
                  onClick={handleReject}
                  className="text-[10px] sm:text-xs tracking-[0.1em] uppercase text-zinc-500 hover:text-white transition-colors px-3 py-2 border border-white/10 hover:border-white/30"
                  data-testid="cookie-reject-btn"
                >
                  {text.reject}
                </button>
                <button
                  onClick={handleAccept}
                  className="text-[10px] sm:text-xs tracking-[0.1em] uppercase text-white bg-white/10 hover:bg-white/20 transition-colors px-4 py-2"
                  data-testid="cookie-accept-btn"
                >
                  {text.accept}
                </button>
              </div>
            </div>
          ) : (
            <div className="pr-8">
              <div className="space-y-4 mb-6">
                {/* Necessary cookies */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white text-xs sm:text-sm font-medium">{text.necessary}</p>
                    <p className="text-zinc-500 text-[10px] sm:text-xs mt-1">{text.necessaryDesc}</p>
                  </div>
                  <div className="w-10 h-5 bg-white/20 rounded-full flex items-center justify-end px-0.5 flex-shrink-0 opacity-50 cursor-not-allowed">
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white text-xs sm:text-sm font-medium">{text.analytics}</p>
                    <p className="text-zinc-500 text-[10px] sm:text-xs mt-1">{text.analyticsDesc}</p>
                  </div>
                  <button
                    onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                    className={`w-10 h-5 rounded-full flex items-center px-0.5 flex-shrink-0 transition-colors ${
                      analyticsEnabled ? 'bg-white/20 justify-end' : 'bg-white/5 justify-start'
                    }`}
                    data-testid="cookie-analytics-toggle"
                  >
                    <div className={`w-4 h-4 rounded-full transition-colors ${analyticsEnabled ? 'bg-white' : 'bg-zinc-600'}`} />
                  </button>
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                className="text-[10px] sm:text-xs tracking-[0.1em] uppercase text-white bg-white/10 hover:bg-white/20 transition-colors px-4 py-2"
                data-testid="cookie-save-btn"
              >
                {text.save}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
